import { type FC, useCallback, useState } from "react";

import { SideSheet } from "../../common/SideSheet";
import { type Tab, Tabs } from "../../common/Tabs";
import { type BlackboardInstance } from "../blackboardModels";
import { InstanceDrawerHeader } from "../InstanceDrawerHeader";
import { useBurnRate } from "../useBurnRate";
import { useInstanceStream } from "../useInstanceStream";
import {
  InspectorToolbar,
  SHEET_WIDTH,
  type BreadcrumbItem,
} from "./InspectorChrome";
import { CostTab } from "./tabs/CostTab";
import { InfoTab } from "./tabs/InfoTab";
import { MemoryTab } from "./tabs/MemoryTab";
import { ObservabilityTab } from "./tabs/ObservabilityTab";
import { StreamTab } from "./tabs/StreamTab";

// ---------------------------------------------------------------------------
// ActorSheet — the actor surface: Identity · Stream · Memory · Cost · Calls.
// One of the two stable inspector identities (the other is ChannelSheet).
// Extracted verbatim from the former InspectorSheet dispatcher so the actor
// mode owns its hooks + chrome directly.
//
// Conversations are not a flow here — they live on channels (private +
// shared), opened as the overlay via the `in ▸` pivot. Stream is the actor's
// live activity surface (its own messages included as events). See
// docs/dashboard-ia-reshape.md.
// ---------------------------------------------------------------------------

const ACTOR_TABS: Tab[] = [
  { id: "overview", label: "Identity" },
  { id: "stream", label: "Stream" },
  { id: "memory", label: "Memory" },
  { id: "cost", label: "Cost" },
  { id: "observability", label: "Calls" },
];

interface ActorSheetProps {
  isOpen: boolean;
  instance: BlackboardInstance;
  isGenerating: boolean;
  onClose: () => void;
  ancestors: BreadcrumbItem[];
  onJumpTo: (index: number) => void;
  onInspectChannel: (channelId: string) => void;
  onKillInstance: (instanceId: string) => Promise<void>;
  onRefresh: () => void;
  /** When false, Escape does not dismiss — set when an overlay sheet covers
   *  this base so Esc dismisses only the topmost. */
  escDismisses?: boolean;
  /** Z-index class; default "z-50", pass "z-[60]" for an overlay. */
  zIndexClass?: string;
  /** Close-button label; overlays pass "‹ Back". */
  closeLabel?: string;
}

export const ActorSheet: FC<ActorSheetProps> = ({
  isOpen,
  instance,
  isGenerating,
  onClose,
  ancestors,
  onJumpTo,
  onInspectChannel,
  onKillInstance,
  onRefresh,
  escDismisses = true,
  zIndexClass = "z-50",
  closeLabel = "Close",
}) => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [killing, setKilling] = useState(false);
  const burnRateUsdPerHr = useBurnRate(instance.id, instance.status);
  const { connected, events, eventsOverflowed } = useInstanceStream(
    instance.id,
  );

  // The parent keys this sheet by actor id, so switching actors remounts it —
  // resetting the active tab along with all editable field state. Tab
  // persistence across actors was deliberately traded away: carrying field
  // state across a switch let actor A's unsaved values be saved onto actor B.

  const handleKill = useCallback(async () => {
    setKilling(true);
    try {
      await onKillInstance(instance.id);
      onClose();
    } finally {
      setKilling(false);
    }
  }, [instance.id, onKillInstance, onClose]);

  return (
    <SideSheet
      isOpen={isOpen}
      onClose={onClose}
      title={instance.displayName}
      widthClass={SHEET_WIDTH}
      headerClassName="relative flex-shrink-0"
      hideCloseButton
      escDismisses={escDismisses}
      zIndexClass={zIndexClass}
      closeLabel={closeLabel}
      headerContent={
        <InstanceDrawerHeader
          instance={instance}
          burnRateUsdPerHr={burnRateUsdPerHr}
          isGenerating={isGenerating}
          onClose={onClose}
          closeLabel={closeLabel === "Close" ? undefined : closeLabel}
          onKill={() => void handleKill()}
          killing={killing}
          onLifecycleChange={onRefresh}
        />
      }
    >
      <div className="-mx-3 -my-2.5 flex h-[calc(100%+1.25rem)] flex-col sm:-mx-4 sm:-my-3 sm:h-[calc(100%+1.5rem)]">
        <InspectorToolbar
          ancestors={ancestors}
          currentLabel={instance.displayName}
          onJumpTo={onJumpTo}
        >
          <ChannelPivot
            instance={instance}
            onInspectChannel={onInspectChannel}
          />
        </InspectorToolbar>
        <Tabs
          tabs={ACTOR_TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          ariaLabel="Actor inspector sections"
        />
        {/* Data/tooling tabs (Stream/Cost/Memory/LLM) fill the full sheet
            width — their tables and grids want the room. Overview is a reading
            form, so it gets a centered, capped, scrollable column. */}
        <div className="min-h-0 w-full min-w-0 flex-1">
          {activeTab === "overview" && (
            <div className="mx-auto h-full w-full max-w-3xl overflow-y-auto">
              <InfoTab
                instance={instance}
                onKill={() => void handleKill()}
                killing={killing}
                onChannelsChange={onRefresh}
                onInstanceChange={onRefresh}
                onOpenChannelWorkspace={onInspectChannel}
              />
            </div>
          )}
          {activeTab === "stream" && (
            <StreamTab
              connected={connected}
              events={events}
              eventsOverflowed={eventsOverflowed}
              instanceId={instance.id}
            />
          )}
          {activeTab === "memory" && <MemoryTab instanceId={instance.id} />}
          {activeTab === "cost" && (
            <CostTab instance={instance} burnRateUsdPerHr={burnRateUsdPerHr} />
          )}
          {activeTab === "observability" && (
            <ObservabilityTab instance={instance} />
          )}
        </div>
      </div>
    </SideSheet>
  );
};

// ---------------------------------------------------------------------------
// ChannelPivot — actor `in ▸ #channel` affordance. Resolves the
// channel-only-chat hole: a private-only or freshly-spawned actor still has a
// reachable target.
// ---------------------------------------------------------------------------

export interface ChannelPivotOption {
  id: string;
  label: string;
}

/**
 * Build the pivot list: every shared channel, plus the private channel if it
 * exists. Private is always reachable this way — previously it was hidden
 * whenever a shared channel was present, making the actor's 1:1 conversation
 * inaccessible from the actor sheet for group members.
 */
export function buildChannelPivotOptions(
  instance: Pick<BlackboardInstance, "channels">,
): ChannelPivotOption[] {
  return [
    ...instance.channels
      .filter((c) => c.kind === "shared" && !c.archivedAt)
      .map((c) => ({ id: c.id, label: `#${c.displayName}` })),
    ...instance.channels
      .filter((c) => c.kind === "private" && !c.archivedAt)
      .slice(0, 1)
      .map((c) => ({ id: c.id, label: "private" })),
  ];
}

const ChannelPivot: FC<{
  instance: BlackboardInstance;
  onInspectChannel: (channelId: string) => void;
}> = ({ instance, onInspectChannel }) => {
  const [open, setOpen] = useState(false);
  return (
    <ChannelPivotView
      onInspectChannel={onInspectChannel}
      onToggle={() => setOpen((prev) => !prev)}
      onClose={() => setOpen(false)}
      open={open}
      options={buildChannelPivotOptions(instance)}
    />
  );
};

/** Hookless render of the pivot, so the navigation wiring is exercisable
 *  without a DOM (the click handlers are the whole point of this control). */
export const ChannelPivotView: FC<{
  onClose: () => void;
  onInspectChannel: (channelId: string) => void;
  onToggle: () => void;
  open: boolean;
  options: readonly ChannelPivotOption[];
}> = ({ onClose, onInspectChannel, onToggle, open, options }) => {
  const label = "font-mono text-[10px] uppercase tracking-[0.16em]";

  if (options.length === 0) {
    return (
      <span
        className={`${label} cursor-default text-slate-400 dark:text-slate-600`}
        title="Joining channel…"
      >
        in ▸ —
      </span>
    );
  }

  // One channel (shared or private) — direct button, no dropdown.
  if (options.length === 1) {
    const [only] = options;
    return (
      <button
        type="button"
        onClick={() => onInspectChannel(only.id)}
        className={`${label} text-slate-500 transition-colors hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300`}
      >
        in ▸ {only.label}
      </button>
    );
  }

  // Multiple channels — dropdown. First option is the summary; full list on open.
  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={onToggle}
        className={`${label} inline-flex items-center gap-1 text-slate-500 transition-colors hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300`}
      >
        in ▸ {options[0].label}
        <span aria-hidden className="text-[8px]">
          ⌄
        </span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 max-h-48 w-max min-w-[140px] overflow-y-auto border border-emerald-400/30 bg-white/95 shadow-[4px_4px_0_rgba(15,23,42,0.18)] backdrop-blur-md dark:bg-slate-950/95"
        >
          {options.map((c) => (
            <li key={c.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => {
                  onInspectChannel(c.id);
                  onClose();
                }}
                className="flex w-full items-center px-2 py-1.5 text-left font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-emerald-700 dark:text-slate-300 dark:hover:bg-slate-800/70 dark:hover:text-emerald-200"
              >
                {c.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
